import { usePageRefreshKey } from '../../../../../context/PageRefreshContext';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react';
import { getOccupants } from '../../../../../redux/actions/SocietyPortal/OccupantAction/OccupantAction';
import { getWingsDropdown, getFlatsDropdown } from '../../../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import { selectedSocietyStorer } from '../../../../../utils/webStorer';
import axiosInstance from '../../../../../axios/axiosInstance';
import apiEndpoints from '../../../../../axios/api';
import MemberFilters from './MemberFilters';
import MemberCard from './MemberCard';
import MemberTable from './MemberTable';
import EditOwnerModal from './EditOwnerModal';
import EditTenantModal from './EditTenantModal';
import EditFamilyModal from './EditFamilyModal';
import DeleteMemberModal from './DeleteMemberModal';
import ViewOwnerModal from './ViewOwnerModal';
import TenantStatusModal from './TenantStatusModal';
import TenantHistoryModal from './TenantHistoryModal';
import { PaginationLimit } from '../../../../../components/PaginationLimit';

const EDIT_MODALS = {
    owner: EditOwnerModal,
    tenant: EditTenantModal,
    family: EditFamilyModal,
};

// Normalize owner rows (nested entity/flat) to flat structure matching tenant/family shape
const normalizeOwner = (row) => ({
    occupant_id: row.entity?.occupant_id,
    flat_ownership_id: row.flat_ownership_id,
    first_name: row.entity?.first_name || '',
    last_name: row.entity?.last_name || '',
    entity_type: row.entity?.entity_type,
    company_name: row.entity?.company_name || '',
    phone: row.entity?.phone || '',
    email: row.entity?.email || '',
    role: row.entity?.role || 'owner',
    is_resident: row.is_resident,
    is_primary: row.entity?.is_primary,
    start_date: row.start_date,
    flat: row.flat,
    ownership_share: row.ownership_share,
});

const MemberListPage = ({ roleConfig }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { occupantsData, occupantsPagination, loading } = useSelector(state => state.OccupantReducer);
    const { wingsDropdown, wingsDropdownLoading, flatsDropdown, flatsDropdownLoading } = useSelector(state => state.WingsAndFlatsReducers);

    const societyId = selectedSocietyStorer.getSocietyId();

    // URL query params for filter persistence
    const [urlParams, setUrlParams] = useSearchParams();
    const initialWing = urlParams.get('wingId') || 'all';
    const initialFlat = urlParams.get('flatId') || 'all';

    const [viewMode, setViewMode] = useState('grid');
    const [searchQuery, setSearchQuery] = useState('');
    // Debounced copy of searchQuery — the fetch effect below reads this
    // instead of searchQuery directly so we don't fire an API call on every
    // keystroke. Prior behaviour raced under fast typing and made the
    // list feel like search wasn't working.
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [filterWing, setFilterWing] = useState(initialWing);
    const [filterFlat, setFilterFlat] = useState(initialFlat);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    // Tenants: Active / Inactive filter, the status dialog, and the history
    // dialog (a flat id). historyRefresh reloads history after a status change.
    const showTenancy = Boolean(roleConfig.tenancyHistory);
    const [filterStatus, setFilterStatus] = useState('all');
    const [statusMember, setStatusMember] = useState(null);
    const [historyFlatId, setHistoryFlatId] = useState(null);
    const [historyRefresh, setHistoryRefresh] = useState(0);
    const [searchParams, setSearchParams] = useState({
        page: 1,
        limit: 10,
        society_id: societyId,
        type: roleConfig.role,
    });

    const rawOccupants = occupantsData || [];
    const occupants = roleConfig.role === 'owner'
        ? rawOccupants.map(normalizeOwner)
        : rawOccupants;
    const wings = wingsDropdown || [];
    const flats = flatsDropdown || [];

    // Helper to update URL params without losing existing ones
    const updateUrlParams = (updates) => {
        setUrlParams(prev => {
            const next = new URLSearchParams(prev);
            Object.entries(updates).forEach(([key, val]) => {
                if (val && val !== 'all') {
                    next.set(key, val);
                } else {
                    next.delete(key);
                }
            });
            return next;
        });
    };

    // Fetch wings on mount
    useEffect(() => {
        if (societyId) {
            dispatch(getWingsDropdown({ society_id: societyId }));
        }
    }, [dispatch, societyId]);

    // Fetch flats when wing filter changes
    useEffect(() => {
        const params = {};
        if (filterWing !== 'all') {
            params.wing_id = filterWing;
        }
        dispatch(getFlatsDropdown(params));
    }, [dispatch, filterWing]);

    // Fetch occupants when search params change
    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        dispatch(getOccupants(searchParams));
    }, [dispatch, searchParams, refreshKey]);

    // Debounce the raw searchQuery → debouncedSearch. 300ms is enough to feel
    // instant to a typist while collapsing a burst of keystrokes into one
    // request.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
        return () => clearTimeout(t);
    }, [searchQuery]);

    // Update search params when filters change
    useEffect(() => {
        const newParams = {
            page: 1,
            limit: searchParams.limit,
            society_id: societyId,
            type: roleConfig.role,
        };
        if (debouncedSearch) newParams.search = debouncedSearch;
        if (filterWing !== 'all') newParams.wing_id = filterWing;
        if (filterFlat !== 'all') newParams.flat_id = filterFlat;
        if (showTenancy && filterStatus !== 'all') newParams.status = filterStatus;
        setSearchParams(newParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, filterWing, filterFlat, filterStatus, societyId, roleConfig.role]);

    // Compute stats from current data
    const stats = useMemo(() => {
        const total = occupantsPagination?.total || 0;
        const residingCount = occupants.filter(m => m.is_resident).length;
        // Tenants: counts across the whole search, whatever the status filter.
        const statusCounts = occupantsPagination?.status_counts || {};
        return {
            total,
            residing: residingCount,
            nonResiding: total - residingCount,
            allTenants: statusCounts.all ?? total,
            active: statusCounts.active ?? 0,
            inactive: statusCounts.inactive ?? 0,
        };
    }, [occupants, occupantsPagination]);

    // Build stat cards from config
    const statsCards = useMemo(() => {
        return roleConfig.statsCards.map(card => ({
            ...card,
            value: stats[card.valueKey] ?? 0,
        }));
    }, [roleConfig.statsCards, stats]);

    const handleWingChange = (value) => {
        setFilterWing(value);
        setFilterFlat('all');
        updateUrlParams({ wingId: value, flatId: 'all' });
    };

    const handleFlatChange = (value) => {
        setFilterFlat(value);
        updateUrlParams({ flatId: value });
    };

    const clearFilters = () => {
        setSearchQuery('');
        setDebouncedSearch('');
        setFilterWing('all');
        setFilterFlat('all');
        setFilterStatus('all');
        updateUrlParams({ wingId: 'all', flatId: 'all' });
    };

    const hasActiveFilters = searchQuery || filterWing !== 'all' || filterFlat !== 'all' || filterStatus !== 'all';

    const handlePageChange = (newPage) => {
        setSearchParams(prev => ({ ...prev, page: newPage }));
    };

    const handleView = (member) => {
        setSelectedMember(member);
        setViewModalOpen(true);
    };

    const handleViewClose = () => {
        setViewModalOpen(false);
        setSelectedMember(null);
    };

    const handleEdit = (member) => {
        setSelectedMember(member);
        setEditModalOpen(true);
    };

    const handleEditClose = () => {
        setEditModalOpen(false);
        setSelectedMember(null);
    };

    const handleEditSuccess = () => {
        dispatch(getOccupants(searchParams));
    };

    const handleDelete = (member) => {
        setSelectedMember(member);
        setDeleteModalOpen(true);
    };

    const handleDeleteClose = () => {
        setDeleteModalOpen(false);
        setSelectedMember(null);
    };

    const handleDeleteSuccess = () => {
        dispatch(getOccupants(searchParams));
    };

    const handleStatusSuccess = () => {
        dispatch(getOccupants(searchParams));
        setHistoryRefresh(n => n + 1);
    };

    const handleHistory = (member) => {
        const flatId = member.flat_id || member.flat?.flat_id;
        if (flatId) setHistoryFlatId(flatId);
    };

    // ---------------------------------------------------------------
    // Bulk Excel: template download, full-data download, bulk upload
    // ---------------------------------------------------------------
    const bulkFileInputRef = useRef(null);
    const [templateLoading, setTemplateLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [uploadLoading, setUploadLoading] = useState(false);
    // Per-row outcome of the last bulk upload. The endpoint has always returned
    // an errors array of { row, flat_number, wing_name, error }; it was only
    // console.warn'd, so a failed import gave no way to see which rows broke or
    // why. Held here so it can be shown in a report dialog.
    const [uploadResult, setUploadResult] = useState(null);

    // Blob download helper — the axios response interceptor unwraps to
    // response.data, so `res` is already the Blob. Wrap defensively in
    // case a future interceptor change hands us the full response object.
    const triggerBlobDownload = (res, filename) => {
        const blob = res instanceof Blob
            ? res
            : new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    };

    const handleDownloadTemplate = async () => {
        if (templateLoading) return;
        setTemplateLoading(true);
        try {
            const res = await axiosInstance.get(apiEndpoints.DOWNLOAD_OCCUPANT_TEMPLATE_API, {
                params: { type: roleConfig.role },
                responseType: 'blob'
            });
            triggerBlobDownload(res, `${roleConfig.role}s_template.xlsx`);
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to download template');
        } finally {
            setTemplateLoading(false);
        }
    };

    const handleDownloadAll = async () => {
        if (exportLoading) return;
        setExportLoading(true);
        try {
            const res = await axiosInstance.get(apiEndpoints.DOWNLOAD_OCCUPANTS_EXCEL_API, {
                params: { type: roleConfig.role, society_id: societyId },
                responseType: 'blob'
            });
            triggerBlobDownload(res, `${roleConfig.role}s_${societyId}.xlsx`);
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to download data');
        } finally {
            setExportLoading(false);
        }
    };

    const handleBulkUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || uploadLoading) return;
        setUploadLoading(true);
        try {
            const form = new FormData();
            form.append('file', file);
            form.append('type', roleConfig.role);
            form.append('society_id', societyId);
            const res = await axiosInstance.post(apiEndpoints.BULK_UPLOAD_OCCUPANTS_API, form, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            const created = res?.data?.created ?? 0;
            const failed = res?.data?.failed ?? 0;
            const errors = res?.data?.errors || [];
            setUploadResult({ created, failed, errors });
            if (failed) {
                toast.warn(`${created} imported, ${failed} failed`);
            } else {
                toast.success(`${created} imported`);
            }
            dispatch(getOccupants(searchParams));
        } catch (err) {
            const message = err?.response?.data?.message || err?.message || 'Bulk upload failed';
            // A whole-file rejection (bad columns, unreadable sheet) never
            // reaches the per-row errors array, so report it in the same place.
            setUploadResult({ created: 0, failed: 0, errors: [], message });
            toast.error(message);
        } finally {
            setUploadLoading(false);
            if (bulkFileInputRef.current) bulkFileInputRef.current.value = '';
        }
    };

    const handleLimitChange = (e) => {
        const newLimit = parseInt(e.target.value, 10) || 10;
        setSearchParams(prev => ({ ...prev, page: 1, limit: newLimit }));
    };

    const EditModal = EDIT_MODALS[roleConfig.editModalKey];

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                <div>
                    <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">{roleConfig.pageTitle}</h1>
                    <p className="tw-text-xs tw-text-gray-500">{roleConfig.pageSubtitle}</p>
                </div>
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    {/* Excel: template + download-all + bulk upload — same
                        endpoints across owner/tenant/family, `type` param
                        selects the role. */}
                    <button
                        type="button"
                        onClick={handleDownloadTemplate}
                        disabled={templateLoading}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-border tw-border-gray-200 tw-text-gray-600 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-gray-50 disabled:tw-opacity-50"
                        title="Download a blank Excel template with the expected columns"
                    >
                        <Icon icon={templateLoading ? 'mdi:loading' : 'mdi:file-outline'} className={templateLoading ? 'mdi-spin' : ''} />
                        Template
                    </button>
                    <button
                        type="button"
                        onClick={handleDownloadAll}
                        disabled={exportLoading}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-border tw-border-gray-200 tw-text-gray-600 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-gray-50 disabled:tw-opacity-50"
                        title="Export all rows for this society as Excel"
                    >
                        <Icon icon={exportLoading ? 'mdi:loading' : 'mdi:file-download-outline'} className={exportLoading ? 'mdi-spin' : ''} />
                        Download
                    </button>
                    <label
                        className={`tw-flex tw-items-center tw-gap-1.5 tw-border tw-border-emerald-200 tw-text-emerald-700 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-emerald-50 tw-cursor-pointer ${uploadLoading ? 'tw-opacity-50 tw-cursor-not-allowed' : ''}`}
                        title="Upload an Excel file to bulk-import rows"
                    >
                        <Icon icon={uploadLoading ? 'mdi:loading' : 'mdi:file-upload-outline'} className={uploadLoading ? 'mdi-spin' : ''} />
                        {uploadLoading ? 'Uploading…' : 'Bulk Upload'}
                        <input
                            ref={bulkFileInputRef}
                            type="file"
                            accept=".xls,.xlsx"
                            className="tw-hidden"
                            onChange={handleBulkUpload}
                            disabled={uploadLoading}
                        />
                    </label>
                    <button
                        onClick={() => navigate(roleConfig.addRoute)}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-bg-blue-600 tw-text-white tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-blue-700"
                    >
                        <Icon icon="mdi:plus" /> {roleConfig.addButtonLabel}
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className={`tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 ${statsCards.length > 3 ? 'md:tw-grid-cols-6' : `md:tw-grid-cols-${statsCards.length}`} tw-gap-3 tw-mb-4`}>
                {statsCards.map((stat, i) => (
                    <div key={i} className="tw-bg-white tw-rounded-lg tw-px-3 tw-py-2.5 tw-shadow-sm tw-border tw-border-gray-100">
                        <div className="tw-flex tw-items-center tw-gap-2">
                            <div className={`tw-w-9 tw-h-9 tw-rounded-lg ${stat.color} tw-flex tw-items-center tw-justify-center tw-flex-shrink-0`}>
                                <Icon icon={stat.icon} className="tw-text-white tw-text-base" />
                            </div>
                            <div className="tw-min-w-0">
                                <div className="tw-flex tw-items-baseline tw-gap-1">
                                    <span className="tw-text-base tw-font-bold tw-text-gray-800">{stat.value}</span>
                                    <span className="tw-text-[11px] tw-text-gray-500 tw-truncate">{stat.label}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <MemberFilters
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                filterWing={filterWing}
                onWingChange={handleWingChange}
                filterFlat={filterFlat}
                onFlatChange={handleFlatChange}
                wings={wings}
                flats={flats}
                wingsLoading={wingsDropdownLoading}
                flatsLoading={flatsDropdownLoading}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                onClearFilters={clearFilters}
                hasActiveFilters={hasActiveFilters}
                showing={occupants.length}
                total={occupantsPagination?.total || occupants.length}
            />

            {showTenancy && (
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
                    <span className="tw-text-xs tw-text-gray-500">Status:</span>
                    {[
                        { key: 'all', label: 'All', count: stats.allTenants },
                        { key: 'active', label: 'Active', count: stats.active },
                        { key: 'inactive', label: 'Inactive', count: stats.inactive },
                    ].map(opt => (
                        <button
                            key={opt.key}
                            type="button"
                            onClick={() => setFilterStatus(opt.key)}
                            className={`tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium tw-border tw-transition-colors ${filterStatus === opt.key
                                ? 'tw-bg-blue-600 tw-border-blue-600 tw-text-white'
                                : 'tw-bg-white tw-border-gray-200 tw-text-gray-600 hover:tw-bg-gray-50'}`}
                        >
                            {opt.label} ({opt.count})
                        </button>
                    ))}
                </div>
            )}

            {/* Loading State */}
            {loading && (
                <div className="tw-flex tw-justify-center tw-items-center tw-py-10">
                    <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-blue-600"></div>
                </div>
            )}

            {/* Grid View */}
            {!loading && viewMode === 'grid' && (
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-4">
                    {occupants.map((member) => (
                        <MemberCard
                            key={member.flat_ownership_id || member.occupant_id}
                            member={member}
                            onView={handleView}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            showTenancy={showTenancy}
                            onToggleStatus={setStatusMember}
                            onHistory={handleHistory}
                        />
                    ))}
                    {occupants.length === 0 && (
                        <div className="tw-col-span-full tw-text-center tw-py-8">
                            <Icon icon="mdi:account-group-outline" className="tw-text-3xl tw-text-gray-300" />
                            <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">No members found</p>
                        </div>
                    )}
                </div>
            )}

            {/* List View */}
            {!loading && viewMode === 'list' && (
                <MemberTable
                    members={occupants}
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    showTenancy={showTenancy}
                    onToggleStatus={setStatusMember}
                    onHistory={handleHistory}
                />
            )}

            {/* Pagination — shared PaginationLimit component includes a
                page-size selector (10 / 20 / 30 …) plus prev / next / numbered
                pages, so short lists still show the record count and long
                lists let the user bump the limit. */}
            {!loading && (occupantsPagination?.total ?? occupants.length) > 0 && (
                <PaginationLimit
                    currentPage={occupantsPagination?.page || searchParams.page || 1}
                    totalRecords={occupantsPagination?.total || occupants.length}
                    limit={occupantsPagination?.limit || searchParams.limit || 10}
                    handlePageChange={handlePageChange}
                    handleLimitChange={handleLimitChange}
                />
            )}

            {/* Edit Modal */}
            {EditModal && (
                <EditModal
                    open={editModalOpen}
                    handleClose={handleEditClose}
                    member={selectedMember}
                    wings={wings}
                    onSuccess={handleEditSuccess}
                />
            )}

            {/* View modal — same component, role-aware. Previously only
                rendered for owners, which made the tenant/family View button
                a no-op. */}
            <ViewOwnerModal
                open={viewModalOpen}
                handleClose={handleViewClose}
                member={selectedMember}
                type={roleConfig.role}
            />

            {/* Delete Modal */}
            <DeleteMemberModal
                open={deleteModalOpen}
                handleClose={handleDeleteClose}
                member={selectedMember}
                type={roleConfig.role?.toUpperCase()}
                onSuccess={handleDeleteSuccess}
            />

            {showTenancy && historyFlatId && (
                <TenantHistoryModal
                    flatId={historyFlatId}
                    refreshToken={historyRefresh}
                    onClose={() => setHistoryFlatId(null)}
                    onToggleStatus={setStatusMember}
                />
            )}

            {/* Rendered after the history dialog so it opens on top of it. */}
            {showTenancy && statusMember && (
                <TenantStatusModal
                    member={statusMember}
                    onClose={() => setStatusMember(null)}
                    onSuccess={handleStatusSuccess}
                />
            )}

            {/* Bulk upload report — every rejected row with the server's reason. */}
            {uploadResult && (
                <div
                    className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4"
                    onClick={() => setUploadResult(null)}
                >
                    <div
                        className="tw-bg-white tw-rounded-xl tw-w-full tw-max-w-2xl tw-shadow-xl tw-max-h-[85vh] tw-flex tw-flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-gray-100">
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <Icon
                                    icon={uploadResult.failed || uploadResult.message ? 'mdi:alert-circle-outline' : 'mdi:check-circle-outline'}
                                    className={`tw-text-xl ${uploadResult.failed || uploadResult.message ? 'tw-text-rose-600' : 'tw-text-emerald-600'}`}
                                />
                                <h3 className="tw-font-semibold tw-text-gray-800 tw-text-sm">Bulk Upload Report</h3>
                            </div>
                            <button onClick={() => setUploadResult(null)} className="tw-p-1 tw-rounded-full hover:tw-bg-gray-100">
                                <Icon icon="mdi:close" className="tw-text-base tw-text-gray-400" />
                            </button>
                        </div>

                        <div className="tw-p-5 tw-overflow-y-auto">
                            {uploadResult.message && (
                                <p className="tw-text-sm tw-text-rose-700 tw-bg-rose-50 tw-border tw-border-rose-100 tw-rounded-lg tw-p-3 tw-mb-4">
                                    {uploadResult.message}
                                </p>
                            )}

                            <div className="tw-flex tw-gap-3 tw-mb-4">
                                <div className="tw-flex-1 tw-bg-emerald-50 tw-border tw-border-emerald-100 tw-rounded-lg tw-p-3">
                                    <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide">Imported</p>
                                    <p className="tw-text-lg tw-font-bold tw-text-emerald-700">{uploadResult.created}</p>
                                </div>
                                <div className="tw-flex-1 tw-bg-rose-50 tw-border tw-border-rose-100 tw-rounded-lg tw-p-3">
                                    <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide">Failed</p>
                                    <p className="tw-text-lg tw-font-bold tw-text-rose-700">{uploadResult.failed}</p>
                                </div>
                            </div>

                            {uploadResult.errors?.length > 0 && (
                                <div className="tw-overflow-x-auto tw-border tw-border-gray-100 tw-rounded-lg">
                                    <table className="tw-w-full tw-text-xs">
                                        <thead className="tw-bg-gray-50">
                                            <tr>
                                                <th className="tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-text-gray-500">Row</th>
                                                <th className="tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-text-gray-500">Wing / Flat</th>
                                                <th className="tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-text-gray-500">Reason</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {uploadResult.errors.map((er, i) => (
                                                <tr key={i} className="tw-border-t tw-border-gray-100">
                                                    <td className="tw-px-3 tw-py-2 tw-text-gray-500">{er.row}</td>
                                                    <td className="tw-px-3 tw-py-2 tw-text-gray-600">
                                                        {[er.wing_name, er.flat_number].filter(Boolean).join(' / ') || '-'}
                                                    </td>
                                                    <td className="tw-px-3 tw-py-2 tw-text-rose-700">{er.error || er.message}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {!uploadResult.message && uploadResult.failed > 0 && !uploadResult.errors?.length && (
                                <p className="tw-text-xs tw-text-gray-500">
                                    The server reported failures but returned no per-row detail.
                                </p>
                            )}
                        </div>

                        <div className="tw-px-5 tw-py-3 tw-border-t tw-border-gray-100 tw-text-end">
                            <button
                                onClick={() => setUploadResult(null)}
                                className="tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-text-white tw-bg-blue-600 tw-rounded-md hover:tw-bg-blue-700"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default MemberListPage;
